import React from 'react';
import { View, Text, DimensionValue } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';

interface OwnershipSegment {
  label: string;
  percentage: number;
  color?: string;
  isCurrentUser?: boolean;
}

interface OwnershipBarProps {
  segments: OwnershipSegment[];
  height?: number;
  showLabels?: boolean;
  showLegend?: boolean;
  style?: any;
}

export function OwnershipBar({ 
  segments, 
  height = 24, 
  showLabels = true,
  showLegend = true,
  style 
}: OwnershipBarProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isSmallMobile } = useResponsive();

  // Default colors for segments - modern, distinguishable palette
  const defaultColors = [
    tokens.color.primary,
    '#8B5CF6', // violet
    '#06B6D4', // cyan
    tokens.color.warning || '#F59E0B', // amber (use warning if available)
    '#EC4899', // pink
    '#84CC16', // lime
    '#F97316', // orange
  ];

  // Calculate total and normalize if needed
  const total = segments.reduce((sum, s) => sum + s.percentage, 0);
  const unassigned = Math.max(0, 100 - total);

  return (
    <View style={style}>
      {/* Bar */}
      <View style={{
        height,
        borderRadius: height / 2,
        backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
        flexDirection: 'row',
        overflow: 'hidden',
      }}>
        {segments.map((segment, index) => {
          const color = segment.color || defaultColors[index % defaultColors.length];
          
          return (
            <View
              key={`${segment.label}-${index}`}
              style={{
                flex: segment.percentage,
                height: '100%',
                backgroundColor: segment.isCurrentUser 
                  ? tokens.color.primary 
                  : color,
                borderRightWidth: index < segments.length - 1 ? 1 : 0,
                borderRightColor: addColorOpacity('#fff', 0.3),
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {showLabels && segment.percentage >= 10 && (
                <Text style={{
                  color: '#fff',
                  fontSize: isSmallMobile ? 10 : 12,
                  fontWeight: '700',
                  textShadowColor: 'rgba(0,0,0,0.3)',
                  textShadowOffset: { width: 0, height: 1 },
                  textShadowRadius: 2,
                }}>
                  {segment.percentage.toFixed(0)}%
                </Text>
              )}
            </View>
          );
        })}
        
        {/* Unassigned portion */}
        {unassigned > 0 && (
          <View style={{
            flex: unassigned,
            height: '100%',
            backgroundColor: 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {showLabels && unassigned >= 15 && (
              <Text style={{
                color: addColorOpacity(tokens.color.onSurface, 0.4),
                fontSize: isSmallMobile ? 10 : 12,
                fontWeight: '600',
              }}>
                {unassigned.toFixed(0)}%
              </Text>
            )}
          </View>
        )}
      </View>

      {/* Legend */}
      {showLegend && (
        <View style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: utils.spacing.md,
          marginTop: utils.spacing.md,
        }}>
          {segments.map((segment, index) => {
            const color = segment.color || defaultColors[index % defaultColors.length];
            return (
              <View 
                key={`legend-${segment.label}-${index}`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: utils.spacing.xs,
                }}
              >
                <View style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  backgroundColor: segment.isCurrentUser ? tokens.color.primary : color,
                }} />
                <Text style={{
                  fontSize: isSmallMobile ? 11 : 13,
                  color: tokens.color.onSurface,
                  fontWeight: segment.isCurrentUser ? '600' : '400',
                }}>
                  {segment.label}
                </Text>
                <Text style={{
                  fontSize: isSmallMobile ? 11 : 13,
                  color: addColorOpacity(tokens.color.onSurface, 0.6),
                }}>
                  ({segment.percentage.toFixed(1)}%)
                </Text>
              </View>
            );
          })}
          
          {unassigned > 0 && (
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: utils.spacing.xs,
            }}>
              <View style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
                borderWidth: 1,
                borderColor: addColorOpacity(tokens.color.onSurface, 0.2),
                borderStyle: 'dashed',
              }} />
              <Text style={{
                fontSize: isSmallMobile ? 11 : 13,
                color: addColorOpacity(tokens.color.onSurface, 0.5),
                fontStyle: 'italic',
              }}>
                Unassigned ({unassigned.toFixed(1)}%)
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

// Simple single-person ownership bar
interface SimpleOwnershipBarProps {
  percentage: number;
  color?: string;
  height?: number;
  showPercentage?: boolean;
}

export function SimpleOwnershipBar({ 
  percentage, 
  color,
  height = 8,
  showPercentage = true,
}: SimpleOwnershipBarProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const barColor = color || tokens.color.primary;

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: utils.spacing.sm }}>
      <View style={{
        flex: 1,
        height,
        borderRadius: height / 2,
        backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
        overflow: 'hidden',
      }}>
        <View style={{
          width: `${Math.min(percentage, 100)}%`,
          height: '100%',
          backgroundColor: barColor,
          borderRadius: height / 2,
        }} />
      </View>
      {showPercentage && (
        <Text style={{
          fontSize: 13,
          fontWeight: '600',
          color: barColor,
          minWidth: 45,
          textAlign: 'right',
        }}>
          {percentage.toFixed(1)}%
        </Text>
      )}
    </View>
  );
}

